import { useState } from 'react'

import { api } from '../api/client'
import type { EagleSonda } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { Card, ErrorBanner, Spinner } from './common'
import { DataTable, type Column } from './DataTable'

const RUOLI: { key: EagleSonda['role']; label: () => string }[] = [
  { key: 'auto', label: () => t('Normale: legge i CI che ha scoperto') },
  { key: 'dedicated', label: () => t('Dedicata al monitoraggio') },
  { key: 'off', label: () => t('Esclusa da Eagle') },
]

/**
 * Le sonde viste da Eagle (ADR-0022): il ruolo di ognuna, le reti che una
 * sonda dedicata raggiunge, quanti CI legge adesso da punto principale e da
 * punto secondario.
 */
export function EagleSonde({ puoModificare }: { puoModificare: boolean }) {
  const dati = useAsync(() => api.eagleSonde(), [])
  const [sonde, setSonde] = useState<EagleSonda[] | null>(null)
  const [aperta, setAperta] = useState<string | null>(null)
  const [ruolo, setRuolo] = useState<EagleSonda['role']>('auto')
  const [reti, setReti] = useState('')
  const [errore, setErrore] = useState<unknown>(null)
  const [invio, setInvio] = useState(false)
  const elenco = sonde ?? dati.data?.probes ?? []

  if (dati.loading && !dati.data) return <Spinner />
  if (!dati.data?.licensed) return null

  function modifica(s: EagleSonda) {
    setAperta(s.id)
    setRuolo(s.role)
    setReti(s.networks.join('\n'))
    setErrore(null)
  }

  async function salva(id: string, togliTetto = false) {
    setInvio(true)
    setErrore(null)
    try {
      const networks = reti.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean)
      const r = await api.eagleSondaRuolo(id, {
        role: ruolo, networks: ruolo === 'dedicated' ? networks : [], reset_max_params: togliTetto,
      })
      setSonde(r.probes)
      setAperta(null)
    } catch (e) {
      setErrore(e)
    } finally {
      setInvio(false)
    }
  }

  const etichetta = (r: EagleSonda['role']) => RUOLI.find((x) => x.key === r)?.label() ?? r

  return (
    <Card title={t('Sonde')}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('Seleziona la sonda da dedicare al monitoraggio Eagle. Indica le reti da monitorare; se non ne specifichi nessuna, la sonda monitora tutti i CI. La sonda di origine resta disponibile per la discovery. La sonda esclusa da Eagle non acquisisce dati di monitoraggio.')}
      </p>
      <ErrorBanner error={errore ?? dati.error} />
      <DataTable
        rows={elenco}
        rowKey={(s) => s.id}
        conteggio={{ nome: t('sonde') }}
        columns={[
          {
            key: 'name', header: t('Sonda'),
            value: (s) => [s.name, s.alive === false ? t('non attiva') : s.alive === null ? t('esclusa da Eagle') : ''].filter(Boolean).join(' · '),
            render: (s) => (
              <>
                {s.name}
                {s.alive === false && <span className="muted"> · {t('non attiva')}</span>}
                {s.alive === null && <span className="muted"> · {t('esclusa da Eagle')}</span>}
              </>
            ),
          },
          {
            key: 'role', header: t('Ruolo'), value: (s) => etichetta(s.role),
            render: (s) => (aperta === s.id ? (
              <select aria-label={t('Ruolo di {sonda}', { sonda: s.name })} value={ruolo}
                      onChange={(e) => setRuolo(e.target.value as EagleSonda['role'])}>
                {RUOLI.map((r) => <option key={r.key} value={r.key}>{r.label()}</option>)}
              </select>
            ) : etichetta(s.role)),
          },
          {
            key: 'networks', header: t('Reti'),
            value: (s) => (s.role === 'dedicated' ? (s.networks.length ? s.networks.join(', ') : t('tutte')) : ''),
            render: (s) => (
              aperta === s.id && ruolo === 'dedicated' ? (
                <textarea aria-label={t('Reti di {sonda}', { sonda: s.name })} rows={3} value={reti}
                          placeholder="10.0.0.0/8" onChange={(e) => setReti(e.target.value)} />
              ) : s.role === 'dedicated' ? (
                <span className="mono">{s.networks.length ? s.networks.join(', ') : t('tutte')}</span>
              ) : (
                <span className="muted">—</span>
              )
            ),
          },
          {
            key: 'cis', header: t('CI letti'), type: 'number', align: 'left',
            value: (s) => s.primary_cis + s.secondary_cis,
            render: (s) => t('{p} principali · {s} secondari', { p: s.primary_cis, s: s.secondary_cis }),
          },
          {
            key: 'load', header: t('Carico'), type: 'number', align: 'left',
            value: (s) => (s.load && s.load.readings ? Math.round((s.load.late / s.load.readings) * 100) : null),
            render: (s) => (
              <>
                <Carico sonda={s} />
                {s.max_params != null && puoModificare && aperta !== s.id && (
                  <div>
                    <button type="button" className="linklike" disabled={invio}
                            onClick={() => { setRuolo(s.role); setReti(s.networks.join('\n')); void salva(s.id, true) }}>
                      {t('Togli il tetto')}
                    </button>
                  </div>
                )}
              </>
            ),
          },
          ...(puoModificare ? [{
            key: 'actions', header: <span aria-label={t('Azioni')} />, sort: false, filter: false, searchable: false,
            render: (s: EagleSonda) => (aperta === s.id ? (
              <span style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={() => salva(s.id)} disabled={invio}>{t('Salva')}</button>
                <button type="button" className="secondary" onClick={() => setAperta(null)} disabled={invio}>{t('Annulla')}</button>
              </span>
            ) : (
              <button type="button" className="secondary" onClick={() => modifica(s)}>{t('Modifica')}</button>
            )),
          } satisfies Column<EagleSonda>] : []),
        ]}
      />
    </Card>
  )
}

/**
 * Quanto regge la sonda: letture in ritardo nell'ultimo lotto e, se c'è, il
 * tetto di parametri fissato dal trasferimento del carico con il motivo.
 */
function Carico({ sonda }: { sonda: EagleSonda }) {
  const c = sonda.load
  if (!c || !c.readings) return <span className="muted">—</span>
  const quota = Math.round((c.late / c.readings) * 100)
  return (
    <span>
      <span className={`badge badge--${quota > 20 ? 'error' : quota > 2 ? 'warn' : 'ok'}`}>
        {t('{n}% in ritardo', { n: quota })}
      </span>
      <span className="muted eagle-piccolo"> · {t('{n} parametri', { n: c.params })}</span>
      {sonda.max_params != null && (
        <div className="eagle-motivo">
          {t('Tetto: {n} parametri', { n: sonda.max_params })}
          {c.cap_reason === 'late' &&
            ` · ${t('fissato: {n} letture su {m} in ritardo', { n: c.cap_late ?? 0, m: c.cap_readings ?? 0 })}`}
          {c.cap_reason === 'loosened' && ` · ${t('allentato: nessun ritardo')}`}
        </div>
      )}
    </span>
  )
}
